import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, Sparkles, Filter, Minus, Plus, Mail, PhoneCall,
  Download, ShoppingCart, Users, MapPin, Eye, X, Megaphone, Monitor, Info, BadgePercent, Wand2, Coins,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar, Footer } from "@/components/Chrome";
import { useAuth, startLogin } from "@/context/AuthContext";
import {
  api, API, money, money2, num, OBJECTIVES, AGE_RANGES, GENDERS, NSE,
  RETAILER_TYPES, BUDGETS, INDUSTRIES, STAGES,
} from "@/lib/spott";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QuoteLocked } from "@/components/QuoteLocked";

const STEPS = ["Objetivo", "Recomendación", "Espacios", "Servicios", "Duración", "Cotización"];

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
      active
        ? "border-spott-blue bg-spott-blue text-white"
        : "border-spott-mid bg-white text-spott-blue hover:border-spott-yellow"
    }`}>
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium text-spott-blue">{label}</Label>
    {children}
  </div>
);

const SpaceCard = ({ s, selected, onToggle, reasons, units, onUnits }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
    className={`group overflow-hidden rounded-xl border bg-white transition-transform hover:-translate-y-1 ${
      selected ? "border-spott-yellow ring-2 ring-spott-yellow/40" : "border-spott-mid"
    }`} data-testid={`space-card-${s.space_id}`}>
    <div className="relative h-40 overflow-hidden bg-spott-light">
      {s.images?.[0] && (
        <img src={s.images[0]} alt={s.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      )}
      <span className="absolute left-3 top-3 rounded-full bg-spott-blue/90 px-3 py-1 text-xs font-medium text-spott-yellow">
        {s.format}
      </span>
      <span className={`absolute left-3 bottom-3 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
        s.funnel_stage === "awareness" ? "bg-spott-yellow text-spott-blue" : "bg-white text-spott-blue"
      }`} data-testid={`space-stage-${s.space_id}`}>
        {s.funnel_stage === "awareness" ? "Awareness" : "Conversión"}
      </span>
      {selected && (
        <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-spott-yellow">
          <Check size={15} className="text-spott-blue" />
        </span>
      )}
    </div>
    <div className="p-5">
      <h3 className="text-base font-semibold leading-snug text-spott-blue">{s.name}</h3>
      <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
        <MapPin size={12} /> {s.retailer_name} · {s.city}
      </p>
      {s.is_digital && s.format_specs && (
        <p className="mt-3 flex gap-2 rounded-lg bg-spott-light px-3 py-2 text-[11px] text-gray-600"
          data-testid={`space-specs-${s.space_id}`}>
          <Monitor size={13} className="mt-0.5 shrink-0 text-spott-yellow" /> {s.format_specs}
        </p>
      )}
      {reasons && (
        <ul className="mt-3 space-y-1.5">
          {reasons.map((r) => (
            <li key={r} className="flex gap-2 text-xs text-gray-600">
              <Sparkles size={12} className="mt-0.5 shrink-0 text-spott-yellow" /> {r}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex items-end justify-between border-t border-spott-mid pt-4">
        <div>
          <div className="text-xs text-gray-500">Alcance/mes</div>
          <div className="text-sm font-semibold text-spott-blue">{num(s.reach_monthly)}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Precio/mes</div>
          <div className="text-lg font-bold text-spott-blue">{money(s.price_month)}</div>
        </div>
      </div>
      <button type="button" onClick={() => onToggle(s.space_id)}
        data-testid={`space-toggle-${s.space_id}`}
        className={`mt-4 w-full rounded-full py-2.5 text-sm font-bold transition-colors ${
          selected ? "bg-spott-blue text-white" : "bg-spott-yellow text-spott-blue hover:bg-amber-400"
        }`}>
        {selected ? "Quitar de la cotización" : "Agregar a la cotización"}
      </button>
      {selected && (
        <div className="mt-3 flex items-center justify-between rounded-lg bg-spott-light px-3 py-2">
          <span className="text-xs font-medium text-spott-blue">Cantidad</span>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => onUnits(s.space_id, units - 1)}
              data-testid={`space-units-minus-${s.space_id}`} aria-label="Menos unidades"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-spott-mid bg-white text-spott-blue">
              <Minus size={13} />
            </button>
            <span className="w-6 text-center text-sm font-bold text-spott-blue"
              data-testid={`space-units-${s.space_id}`}>{units}</span>
            <button type="button" onClick={() => onUnits(s.space_id, units + 1)}
              data-testid={`space-units-plus-${s.space_id}`} aria-label="Más unidades"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-spott-yellow text-spott-blue">
              <Plus size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  </motion.div>
);

export default function Wizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isStaffUser = user && (user.role === "admin" || user.role === "agente");
  const [step, setStep] = useState(0);

  const [brief, setBrief] = useState({
    company_name: "", industry: "", website: "", objectives: [], age_ranges: [],
    genders: [], nse: [], cities: [], retailer_types: [], budget_range: "", budget_reference: "",
  });
  const [spaceIds, setSpaceIds] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [serviceIds, setServiceIds] = useState([]);
  const [months, setMonths] = useState(1);

  const [spaces, setSpaces] = useState([]);
  const [services, setServices] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [filters, setFilters] = useState({ cities: [], formats: [] });
  const [retailers, setRetailers] = useState([]);
  const [recs, setRecs] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [calc, setCalc] = useState(null);
  const [quote, setQuote] = useState(null);
  const [f, setF] = useState({ retailer_id: "", city: "", format: "", q: "", stage: "" });
  const [cfg, setCfg] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [agentModal, setAgentModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [lead, setLead] = useState({ contact_name: "", contact_email: "", contact_phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Cotizador en vivo — Spott";
    (async () => {
      const [sp, sv, fl, rt, dc, st] = await Promise.all([
        api.get("/spaces"), api.get("/services"), api.get("/filters"),
        api.get("/retailers"), api.get("/discounts"), api.get("/settings"),
      ]);
      setSpaces(sp.data); setServices(sv.data); setFilters(fl.data);
      setRetailers(rt.data); setDiscounts(dc.data); setCfg(st.data);
    })();
  }, []);

  useEffect(() => {
    if (user) setBrief((b) => ({ ...b, company_name: b.company_name || user.company_name || "", industry: b.industry || user.industry || "" }));
    if (user) { setEmailTo((e) => e || user.email); setLead((l) => ({ ...l, contact_name: l.contact_name || user.name, contact_email: l.contact_email || user.email })); }
  }, [user]);

  // load a draft to resume
  useEffect(() => {
    const id = params.get("retomar");
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${id}`);
        setBrief({
          company_name: data.company_name || "", industry: data.industry || "", website: data.website || "",
          objectives: data.objectives || [], age_ranges: data.age_ranges || [], genders: data.genders || [],
          nse: data.nse || [], cities: data.cities || [], retailer_types: data.retailer_types || [],
          budget_range: data.budget_range || "",
          budget_reference: data.budget_reference || "",
        });
        setSpaceIds((data.space_items || []).map((i) => i.space_id));
        setQuantities(Object.fromEntries((data.space_items || []).map((i) => [i.space_id, i.units || 1])));
        setServiceIds((data.service_items || []).map((i) => i.service_id));
        setMonths(data.duration_months || 1);
        setStep(2);
        toast.success("Borrador cargado");
      } catch {
        toast.error("No se pudo cargar el borrador");
      }
    })();
  }, [params]);

  const recalc = useCallback(async () => {
    if (!spaceIds.length && !serviceIds.length) { setCalc(null); return; }
    const { data } = await api.post("/quotes/calculate", {
      brief: { ...brief, budget_reference: Number(brief.budget_reference) || 0 },
      space_ids: spaceIds, quantities, service_ids: serviceIds, duration_months: months,
    });
    setCalc(data);
  }, [spaceIds, quantities, serviceIds, months, brief]);

  useEffect(() => { recalc(); }, [recalc]);

  const filtered = useMemo(
    () => spaces.filter((s) =>
      (!f.retailer_id || s.retailer_id === f.retailer_id) &&
      (!f.city || s.city === f.city) &&
      (!f.format || s.format === f.format) &&
      (!f.stage || s.funnel_stage === f.stage) &&
      (!f.q || s.name.toLowerCase().includes(f.q.toLowerCase()))),
    [spaces, f]
  );

  const step1Valid = brief.company_name.trim() && brief.industry;

  const toggleSpace = (id) => {
    if (spaceIds.includes(id)) {
      setSpaceIds(spaceIds.filter((x) => x !== id));
      const q = { ...quantities }; delete q[id]; setQuantities(q);
    } else {
      setSpaceIds([...spaceIds, id]);
      setQuantities({ ...quantities, [id]: 1 });
    }
  };

  const setUnits = (id, v) => {
    const n = Math.max(1, Math.min(99, v));
    setQuantities({ ...quantities, [id]: n });
  };
  const unitsOf = (id) => quantities[id] || 1;
  const totalUnits = spaceIds.reduce((a, id) => a + unitsOf(id), 0);

  const goRecommend = async () => {
    setLoadingRecs(true);
    try {
      const { data } = await api.post("/recommendations", {
        ...brief, budget_reference: Number(brief.budget_reference) || 0,
      });
      setRecs(data);
      setStep(1);
    } catch {
      toast.error("No pudimos calcular las recomendaciones. Intenta de nuevo.");
    } finally {
      setLoadingRecs(false);
    }
  };

  const buildAutoPlan = async () => {
    const budget = Number(brief.budget_reference) || 0;
    if (budget <= 0) { toast.error("Ingresa un presupuesto referencial en el paso 1"); setStep(0); return; }
    setPlanning(true);
    try {
      const { data } = await api.post("/plan/auto", {
        brief: { ...brief, budget_reference: budget }, duration_months: months, budget,
      });
      setPlan(data);
      setSpaceIds(data.space_ids);
      setQuantities(data.quantities);
      toast.success(`Plan armado: ${data.items.length} espacios usando el ${data.budget_usage_percent}% de tu presupuesto`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos armar el plan automático");
    } finally { setPlanning(false); }
  };

  const next = async () => {
    if (step === 0) {
      if (!step1Valid) { toast.error("Ingresa el nombre de tu marca y la industria"); return; }
      await goRecommend();
      return;
    }
    if (step === 2 && !spaceIds.length) { toast.error("Selecciona al menos un espacio publicitario"); return; }
    if (step === 4) { await ensureQuote(); }
    setStep((s) => Math.min(5, s + 1));
  };

  const ensureQuote = async () => {
    const payload = {
      brief: { ...brief, budget_reference: Number(brief.budget_reference) || 0 },
      space_ids: spaceIds, quantities, service_ids: serviceIds, duration_months: months,
      status: "borrador",
      contact_name: user?.name || lead.contact_name, contact_email: user?.email || lead.contact_email,
    };
    if (quote) {
      const { data } = await api.put(`/quotes/${quote.quote_id}`, payload);
      setQuote(data);
      return data;
    }
    const { data } = await api.post("/quotes", payload);
    setQuote(data);
    return data;
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const q = await ensureQuote();
      await api.post("/quotes/send-email", { quote_id: q.quote_id, email: emailTo });
      toast.success(`Cotización enviada a ${emailTo}`);
      setEmailModal(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la cotización");
    } finally { setBusy(false); }
  };

  const requestAgent = async () => {
    if (!lead.contact_name || !lead.contact_email) { toast.error("Ingresa tu nombre y correo"); return; }
    setBusy(true);
    try {
      const q = await ensureQuote();
      await api.post("/leads", { quote_id: q.quote_id, ...lead });
      toast.success("¡Listo! Un agente comercial de Spott te contactará en breve.");
      setAgentModal(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar tu solicitud");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    const q = await ensureQuote();
    window.open(`${API}/quotes/${q.quote_id}/pdf`, "_blank");
  };

  const selectedSpaces = spaces.filter((s) => spaceIds.includes(s.space_id));

  if (!isStaffUser) return <QuoteLocked />;

  return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />

      {/* promo banner */}
      {cfg?.promo_active && cfg.promo_title && (
        <div className="border-b border-spott-yellow/40 bg-spott-yellow" data-testid="promo-banner">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-5 py-3 lg:px-10">
            <Megaphone size={18} className="shrink-0 text-spott-blue" />
            <span className="text-sm font-bold text-spott-blue" data-testid="promo-title">{cfg.promo_title}</span>
            <span className="text-xs text-spott-blue/80">{cfg.promo_text}</span>
            {cfg.promo_cta_label && (
              <button onClick={() => { setStep(2); setF({ ...f, q: "" }); }} data-testid="promo-cta"
                className="ml-auto rounded-full bg-spott-blue px-4 py-2 text-xs font-bold text-white">
                {cfg.promo_cta_label}
              </button>
            )}
          </div>
        </div>
      )}

      {/* progress */}
      <div className="sticky top-[72px] z-40 border-b border-spott-mid bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 py-4 lg:px-10">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="wizard-progress">
            {STEPS.map((s, i) => (
              <div key={s} className="flex shrink-0 items-center gap-1">
                <button onClick={() => i < step && setStep(i)} aria-current={i === step ? "step" : undefined}
                  data-testid={`progress-step-${i}`}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    i === step ? "bg-spott-yellow text-spott-blue"
                      : i < step ? "text-spott-blue hover:bg-spott-light" : "text-gray-400"
                  }`}>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${
                    i <= step ? "border-spott-blue bg-spott-blue text-white" : "border-spott-mid"
                  }`}>{i < step ? <Check size={12} /> : i + 1}</span>
                  <span className="hidden sm:inline">{s}</span>
                </button>
                {i < STEPS.length - 1 && <span className="h-px w-4 bg-spott-mid sm:w-8" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-10 lg:grid-cols-12 lg:px-10 lg:py-14">
        <main className="lg:col-span-8">
          {/* STEP 1 */}
          {step === 0 && (
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-spott-mid bg-white p-6 lg:p-9" data-testid="step-brief">
              <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">
                Cuéntanos de tu marca y tu objetivo
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Con esta información nuestro motor prioriza los espacios con mejor ajuste. Solo la marca y la
                industria son obligatorias; el resto es opcional y puedes ajustarlo después.
              </p>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <Field label="Marca / Empresa *">
                  <Input value={brief.company_name} data-testid="input-company"
                    onChange={(e) => setBrief({ ...brief, company_name: e.target.value })}
                    placeholder="Ej. Lácteos Andinos" />
                </Field>
                <Field label="Sitio web (opcional)">
                  <Input value={brief.website} data-testid="input-website"
                    onChange={(e) => setBrief({ ...brief, website: e.target.value })}
                    placeholder="www.tumarca.com" />
                </Field>
              </div>
              <div className="mt-6">
                <Label className="text-sm font-medium text-spott-blue">Industria / rubro *</Label>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="industry-chips">
                  {INDUSTRIES.map((i) => (
                    <Chip key={i} active={brief.industry === i} testid={`industry-${i}`}
                      onClick={() => setBrief({ ...brief, industry: i })}>{i}</Chip>
                  ))}
                </div>
              </div>
              <div className="mt-8">
                <Label className="text-sm font-medium text-spott-blue">Objetivos de campaña (opcional)</Label>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="objective-chips">
                  {OBJECTIVES.map((o) => (
                    <Chip key={o.id} active={brief.objectives.includes(o.id)} testid={`objective-${o.id}`}
                      onClick={() => setBrief({ ...brief, objectives: toggle(brief.objectives, o.id) })}>
                      {o.label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="mt-10 rounded-xl bg-spott-light p-6">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-spott-blue">
                  <Users size={18} className="text-spott-yellow" /> Audiencia objetivo (opcional)
                </h2>
                <div className="mt-5 space-y-6">
                  <div>
                    <Label className="text-sm font-medium text-spott-blue">Ciudades / regiones (opcional)</Label>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="city-chips">
                      {filters.cities.map((c) => (
                        <Chip key={c} active={brief.cities.includes(c)} testid={`city-${c}`}
                          onClick={() => setBrief({ ...brief, cities: toggle(brief.cities, c) })}>{c}</Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-spott-blue">Rango de edad</Label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {AGE_RANGES.map((a) => (
                        <Chip key={a} active={brief.age_ranges.includes(a)} testid={`age-${a}`}
                          onClick={() => setBrief({ ...brief, age_ranges: toggle(brief.age_ranges, a) })}>{a}</Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-spott-blue">Género</Label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {GENDERS.map((g) => (
                        <Chip key={g} active={brief.genders.includes(g)} testid={`gender-${g}`}
                          onClick={() => setBrief({ ...brief, genders: toggle(brief.genders, g) })}>{g}</Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-spott-blue">Nivel socioeconómico</Label>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="nse-chips">
                      {NSE.map((n) => (
                        <Chip key={n} active={brief.nse.includes(n)} testid={`nse-${n}`}
                          onClick={() => setBrief({ ...brief, nse: toggle(brief.nse, n) })}>NSE {n}</Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-spott-blue">Tipo de retailer preferido</Label>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="retailer-type-chips">
                      {RETAILER_TYPES.map((r) => (
                        <Chip key={r} active={brief.retailer_types.includes(r)} testid={`rtype-${r}`}
                          onClick={() => setBrief({ ...brief, retailer_types: toggle(brief.retailer_types, r) })}>{r}</Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-spott-blue">Presupuesto estimado (opcional)</Label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {BUDGETS.map((b) => (
                        <Chip key={b.id} active={brief.budget_range === b.id} testid={`budget-${b.id}`}
                          onClick={() => setBrief({ ...brief, budget_range: brief.budget_range === b.id ? "" : b.id })}>
                          {b.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-spott-yellow/50 bg-spott-yellow/10 p-5">
                    <Label className="text-sm font-medium text-spott-blue">
                      ¿Tienes un monto referencial en mente? (opcional)
                    </Label>
                    <div className="mt-3 flex max-w-xs items-center gap-2 rounded-lg border border-spott-mid bg-white px-3">
                      <span className="text-sm font-bold text-spott-blue">USD $</span>
                      <Input type="number" min="0" step="100" value={brief.budget_reference}
                        data-testid="budget-reference-input" placeholder="25000"
                        onChange={(e) => setBrief({ ...brief, budget_reference: e.target.value })}
                        className="border-0 px-0 focus-visible:ring-0" />
                    </div>
                    <p className="mt-3 flex gap-2 text-xs text-gray-600" data-testid="budget-reference-disclaimer">
                      <Info size={14} className="mt-0.5 shrink-0 text-spott-yellow" />
                      Es solo referencial: no limita tu cotización. Spott puede armar un plan de Retail Media
                      que aproveche al máximo ese monto y proponerte la mejor combinación de espacios y
                      servicios disponibles.
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>
          )}

          {/* STEP 2 */}
          {step === 1 && (
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-recommendations">
              <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">
                Espacios recomendados para {brief.company_name}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Priorizados según tus objetivos, ciudades y perfil de audiencia. Puedes agregarlos ahora o
                explorar el catálogo completo en el siguiente paso.
              </p>

              {/* plan automático por presupuesto */}
              <div className="mt-8 overflow-hidden rounded-xl border border-spott-yellow bg-spott-blue text-white"
                data-testid="auto-plan-card">
                <div className="flex flex-wrap items-center justify-between gap-4 p-6">
                  <div className="max-w-xl">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-spott-yellow">
                      <Wand2 size={18} /> Plan automático por presupuesto
                    </h2>
                    <p className="mt-2 text-sm font-light text-spott-mid">
                      {Number(brief.budget_reference) > 0
                        ? `Armamos la mejor combinación de espacios para aprovechar tus ${money(Number(brief.budget_reference))} en ${months} ${months === 1 ? "mes" : "meses"}, incluyendo el fee de servicio y el descuento por permanencia.`
                        : "Ingresa un monto referencial y armamos la mejor combinación de espacios para ese presupuesto."}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2">
                      <span className="text-sm font-bold text-spott-yellow">USD $</span>
                      <input type="number" min="0" step="500" value={brief.budget_reference}
                        data-testid="auto-plan-budget-input" placeholder="25000"
                        onChange={(e) => setBrief({ ...brief, budget_reference: e.target.value })}
                        className="w-28 bg-transparent text-sm font-semibold text-white outline-none placeholder:text-spott-mid" />
                    </div>
                    <button onClick={buildAutoPlan} disabled={planning} data-testid="auto-plan-btn"
                      className="rounded-full bg-spott-yellow px-6 py-3 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03] disabled:opacity-60">
                      {planning ? "Armando…" : plan ? "Recalcular plan" : "Armar mi plan"}
                    </button>
                  </div>
                </div>
                {plan && (
                  <div className="border-t border-white/10 bg-white/5 p-6" data-testid="auto-plan-result">
                    {plan.strategy?.length > 0 && (
                      <div className="mb-5 rounded-lg border border-spott-yellow/40 bg-spott-yellow/10 p-4"
                        data-testid="auto-plan-strategy">
                        <p className="text-xs font-semibold uppercase tracking-wide text-spott-yellow">
                          Recomendación especializada{plan.objectives?.length ? ` · ${plan.objectives.join(" + ")}` : ""}
                        </p>
                        <ul className="mt-2 space-y-1.5">
                          {plan.strategy.map((s) => (
                            <li key={s} className="flex gap-2 text-sm font-light text-white">
                              <Sparkles size={13} className="mt-1 shrink-0 text-spott-yellow" /> {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="grid gap-4 sm:grid-cols-4">
                      {[
                        ["Total del plan", money2(plan.total)],
                        ["Uso del presupuesto", `${plan.budget_usage_percent}%`],
                        ["Puntos Spott", num(plan.points_earned)],
                        ["Impactos estimados", num(plan.total_reach)],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div className="text-xs font-light text-spott-mid">{l}</div>
                          <div className="mt-1 text-lg font-bold text-spott-yellow">{v}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/15">
                      <div className="h-full rounded-full bg-spott-yellow transition-all"
                        style={{ width: `${Math.min(100, plan.budget_usage_percent)}%` }} />
                    </div>
                    <ul className="mt-5 space-y-2">
                      {plan.items.map((i) => (
                        <li key={i.space_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-2 text-sm"
                          data-testid={`auto-plan-item-${i.space_id}`}>
                          <span className="font-medium">
                            <span className="text-spott-yellow">{i.units}×</span> {i.name}
                            <span className="ml-2 text-xs font-light text-spott-mid">
                              {i.retailer_name} · {i.city} · {i.funnel_stage === "awareness" ? "Awareness" : "Conversión"}
                            </span>
                          </span>
                          <span className="font-semibold">{money2(i.total)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 text-xs font-light text-spott-mid">
                      {plan.awareness_count} espacio(s) de awareness y {plan.conversion_count} de conversión ·
                      descuento hasta {plan.discount_percent}% · fee de servicio {plan.service_fee_percent}%
                      ({money2(plan.service_fee)}). Ya cargamos el plan en tu cotización: puedes ajustarlo
                      libremente en los siguientes pasos.
                    </p>
                  </div>
                )}
              </div>

              {loadingRecs ? (
                <div className="mt-10 h-8 w-8 animate-spin rounded-full border-2 border-spott-yellow border-t-transparent" />
              ) : (
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  {recs.map((r) => (
                    <SpaceCard key={r.space_id} s={r} reasons={r.reasons}
                      selected={spaceIds.includes(r.space_id)} units={unitsOf(r.space_id)} onUnits={setUnits}
                      onToggle={toggleSpace} />
                  ))}
                </div>
              )}
            </motion.section>
          )}

          {/* STEP 3 */}
          {step === 2 && (
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-catalog">
              <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">
                Catálogo de espacios publicitarios
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Filtra por etapa del embudo: <strong>Awareness</strong> para notoriedad y alcance masivo
                (pantallas, LED, backlights, activaciones) o <strong>Conversión</strong> para impactar la
                decisión de compra (góndola, islas, sampling, impulsación).
              </p>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="stage-tabs">
                <Chip active={!f.stage} testid="stage-all" onClick={() => setF({ ...f, stage: "" })}>
                  Todos los espacios
                </Chip>
                {STAGES.map((st) => (
                  <Chip key={st.id} active={f.stage === st.id} testid={`stage-${st.id}`}
                    onClick={() => setF({ ...f, stage: st.id })}>
                    {st.label} · {st.hint}
                  </Chip>
                ))}
              </div>
              <div className="mt-6 rounded-xl border border-spott-mid bg-white p-5">
                <div className="flex items-center gap-2 text-sm font-semibold text-spott-blue">
                  <Filter size={16} className="text-spott-yellow" /> Filtros
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <select value={f.retailer_id} onChange={(e) => setF({ ...f, retailer_id: e.target.value })}
                    data-testid="filter-retailer"
                    className="h-10 rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
                    <option value="">Todos los retailers</option>
                    {retailers.map((r) => <option key={r.retailer_id} value={r.retailer_id}>{r.name}</option>)}
                  </select>
                  <select value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })}
                    data-testid="filter-city"
                    className="h-10 rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
                    <option value="">Todas las ciudades</option>
                    {filters.cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })}
                    data-testid="filter-format"
                    className="h-10 rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
                    <option value="">Todos los formatos</option>
                    {filters.formats.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Input placeholder="Buscar espacio…" value={f.q} data-testid="filter-search"
                    onChange={(e) => setF({ ...f, q: e.target.value })} />
                </div>
              </div>
              <p className="mt-5 text-sm text-gray-600" data-testid="catalog-count">
                {filtered.length} espacios disponibles
              </p>
              <div className="mt-5 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="catalog-grid">
                {filtered.map((s) => (
                  <SpaceCard key={s.space_id} s={s} selected={spaceIds.includes(s.space_id)}
                    units={unitsOf(s.space_id)} onUnits={setUnits} onToggle={toggleSpace} />
                ))}
              </div>
            </motion.section>
          )}

          {/* STEP 4 */}
          {step === 3 && (
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-services">
              <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">Servicios adicionales</h1>
              <p className="mt-2 text-sm text-gray-600">
                Spott puede ejecutar la campaña de punta a punta. Los servicios por espacio se multiplican por
                las {totalUnits} unidades seleccionadas.
              </p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                {services.map((sv) => {
                  const on = serviceIds.includes(sv.service_id);
                  const total = sv.price_type === "per_space" ? sv.price * totalUnits : sv.price;
                  return (
                    <div key={sv.service_id} data-testid={`service-card-${sv.service_id}`}
                      className={`rounded-xl border bg-white p-6 transition-transform hover:-translate-y-1 ${
                        on ? "border-spott-yellow ring-2 ring-spott-yellow/40" : "border-spott-mid"
                      }`}>
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base font-semibold text-spott-blue">{sv.name}</h3>
                        <span className="shrink-0 rounded-full bg-spott-light px-3 py-1 text-xs font-medium text-spott-blue">
                          {sv.price_type === "per_space" ? "por espacio" : "fijo"}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">{sv.description}</p>
                      <div className="mt-5 flex items-center justify-between border-t border-spott-mid pt-4">
                        <div>
                          <div className="text-lg font-bold text-spott-blue">{money2(total)}</div>
                          {sv.price_type === "per_space" && (
                            <div className="text-xs text-gray-500">{money2(sv.price)} × {totalUnits}</div>
                          )}
                        </div>
                        <button type="button" onClick={() => setServiceIds(toggle(serviceIds, sv.service_id))}
                          data-testid={`service-toggle-${sv.service_id}`}
                          className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
                            on ? "bg-spott-blue text-white" : "bg-spott-yellow text-spott-blue hover:bg-amber-400"
                          }`}>
                          {on ? "Quitar" : "Agregar"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* STEP 5 */}
          {step === 4 && (
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-spott-mid bg-white p-6 lg:p-9" data-testid="step-duration">
              <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">Duración de la campaña</h1>
              <p className="mt-2 text-sm text-gray-600">Mínimo 1 mes. Mientras más permanencia, mejor tarifa.</p>
              <div className="mt-10 flex items-center justify-center gap-8">
                <button onClick={() => setMonths((m) => Math.max(1, m - 1))} data-testid="months-minus"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-spott-mid text-spott-blue transition-colors hover:border-spott-yellow">
                  <Minus size={20} />
                </button>
                <div className="text-center">
                  <div className="text-6xl font-bold text-spott-blue" data-testid="months-value">{months}</div>
                  <div className="mt-1 text-sm font-light text-gray-500">{months === 1 ? "mes" : "meses"}</div>
                </div>
                <button onClick={() => setMonths((m) => Math.min(36, m + 1))} data-testid="months-plus"
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-spott-yellow text-spott-blue transition-transform hover:scale-105">
                  <Plus size={20} />
                </button>
              </div>
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                {[1, 3, 6, 12].map((m) => (
                  <Chip key={m} active={months === m} testid={`preset-${m}`} onClick={() => setMonths(m)}>
                    {m} {m === 1 ? "mes" : "meses"}
                  </Chip>
                ))}
              </div>
              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {discounts.map((d) => (
                  <div key={d.rule_id} data-testid={`discount-${d.rule_id}`}
                    className={`rounded-xl border p-5 text-center ${
                      months >= d.min_months ? "border-spott-yellow bg-spott-yellow/10" : "border-spott-mid bg-spott-light"
                    }`}>
                    <div className="text-2xl font-bold text-spott-blue">hasta {d.percent}%</div>
                    <div className="mt-1 text-xs text-gray-600">desde {d.min_months} meses</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-center text-xs text-gray-500">
                Los descuentos son máximos y dependen de las condiciones comerciales de cada retailer.
              </p>
            </motion.section>
          )}

          {/* STEP 6 */}
          {step === 5 && calc && (
            <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-summary">
              <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">Tu cotización está lista</h1>
              <p className="mt-2 text-sm text-gray-600">
                {brief.company_name} · {calc.space_items.length} espacios ({calc.total_units} unidades) ·
                {" "}{months} {months === 1 ? "mes" : "meses"}
              </p>

              <div className="mt-6 overflow-hidden rounded-xl border border-spott-mid bg-white">
                <div className="bg-spott-blue px-6 py-4 text-sm font-semibold text-white">Espacios publicitarios</div>
                {calc.space_items.map((i) => (
                  <div key={i.space_id} className="flex items-center justify-between gap-4 border-b border-spott-mid px-6 py-4">
                    <div>
                      <div className="text-sm font-semibold text-spott-blue">{i.name}</div>
                      <div className="text-xs text-gray-500">{i.retailer_name} · {i.city} · {num(i.reach_monthly)} impactos/mes</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-spott-blue">{money2(i.total)}</div>
                      <div className="text-xs text-gray-500">{i.units} × {money2(i.unit_price)}/mes</div>
                    </div>
                  </div>
                ))}
                {calc.service_items.length > 0 && (
                  <>
                    <div className="bg-spott-blue px-6 py-4 text-sm font-semibold text-white">Servicios adicionales</div>
                  {calc.service_items.map((i) => (
                      <div key={i.service_id} className="flex items-center justify-between gap-4 border-b border-spott-mid px-6 py-4">
                        <div className="text-sm font-semibold text-spott-blue">
                          {i.name}
                          {i.is_free && (
                            <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700"
                              data-testid={`service-free-${i.service_id}`}>Incluido sin costo</span>
                          )}
                        </div>
                        <div className="text-sm font-bold text-spott-blue">
                          {i.is_free ? <span className="text-emerald-600">Incluido</span> : money2(i.total)}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                <div className="space-y-2 px-6 py-5 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal espacios</span><span>{money2(calc.spaces_subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal servicios</span><span>{money2(calc.services_subtotal)}</span>
                  </div>
                  {calc.discount_amount > 0 && (
                    <div className="flex justify-between font-medium text-emerald-600">
                      <span>Descuento por permanencia (hasta {calc.discount_percent}%)</span>
                      <span>- {money2(calc.discount_amount)}</span>
                    </div>
                  )}
                  {calc.service_fee > 0 && (
                    <div className="flex justify-between font-medium text-spott-blue" data-testid="summary-fee">
                      <span>{calc.service_fee_label} ({calc.service_fee_percent}%)</span>
                      <span>{money2(calc.service_fee)}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between bg-spott-yellow px-6 py-5">
                  <span className="text-sm font-bold text-spott-blue">TOTAL CAMPAÑA (USD)</span>
                  <span className="text-2xl font-bold text-spott-blue" data-testid="summary-total">{money2(calc.total)}</span>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-spott-mid bg-white p-6">
                  <Eye size={20} className="text-spott-yellow" />
                  <div className="mt-3 text-2xl font-bold text-spott-blue" data-testid="summary-reach">
                    {num(calc.total_reach)}
                  </div>
                  <div className="text-sm text-gray-600">impactos estimados en toda la campaña</div>
                </div>
                <div className="rounded-xl border border-spott-mid bg-white p-6">
                  <ShoppingCart size={20} className="text-spott-yellow" />
                  <div className="mt-3 text-2xl font-bold text-spott-blue">{money2(calc.monthly_cost)}</div>
                  <div className="text-sm text-gray-600">costo estimado por mes</div>
                </div>
                <div className="rounded-xl border border-spott-yellow bg-spott-yellow/10 p-6"
                  data-testid="summary-points">
                  <Coins size={20} className="text-spott-blue" />
                  <div className="mt-3 text-2xl font-bold text-spott-blue" data-testid="summary-points-value">
                    {num(calc.points_earned)} pts
                  </div>
                  <div className="text-sm text-gray-700">
                    Puntos Spott que ganarías al ejecutar esta campaña ({calc.points_per_dollar} punto por dólar).
                    Equivalen a {money2(calc.points_earned / calc.points_per_dollar_redeem)} en espacios de canje.
                  </div>
                  <Link to="/recompensas" data-testid="summary-rewards-link"
                    className="mt-3 inline-block text-xs font-bold text-spott-blue underline">
                    Ver catálogo de canje
                  </Link>
                </div>
              </div>

              <div className="mt-4 space-y-2 rounded-xl border border-spott-mid bg-white p-5 text-xs text-gray-600">
                {calc.discount_amount > 0 && (
                  <p className="flex gap-2" data-testid="discount-disclaimer">
                    <BadgePercent size={14} className="mt-0.5 shrink-0 text-spott-yellow" />
                    Los descuentos por permanencia son máximos ("hasta {calc.discount_percent}%") y dependen de
                    las condiciones comerciales que otorgue cada retailer.
                  </p>
                )}
                {calc.analytics_free && (
                  <p className="flex gap-2" data-testid="analytics-free-note">
                    <Check size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                    Reportería y analytics incluidos sin costo por superar {money(calc.analytics_free_threshold)}
                    {" "}de inversión en espacios.
                  </p>
                )}
                {!calc.analytics_free && (
                  <p className="flex gap-2">
                    <Info size={14} className="mt-0.5 shrink-0 text-spott-yellow" />
                    Sobre {money(calc.analytics_free_threshold)} de inversión en espacios, la reportería y
                    analytics van sin costo.
                  </p>
                )}
                {Number(brief.budget_reference) > 0 && (
                  <p className="flex gap-2" data-testid="budget-reference-note">
                    <Info size={14} className="mt-0.5 shrink-0 text-spott-yellow" />
                    Indicaste un presupuesto referencial de {money(Number(brief.budget_reference))}. Spott puede
                    armar un plan alternativo ajustado a ese monto.
                  </p>
                )}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setEmailModal(true)} data-testid="send-email-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-spott-blue px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]">
                  <Mail size={17} /> Enviar por correo
                </button>
                <button onClick={() => setAgentModal(true)} data-testid="request-agent-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-6 py-3.5 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.02]">
                  <PhoneCall size={17} /> Quiero que un agente me contacte
                </button>
                <button onClick={downloadPdf} data-testid="download-pdf-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-spott-mid bg-white px-6 py-3.5 text-sm font-bold text-spott-blue transition-colors hover:border-spott-yellow">
                  <Download size={17} /> Descargar PDF
                </button>
              </div>

              {!user && (
                <div className="mt-8 rounded-xl border border-spott-mid bg-white p-6" data-testid="create-account-cta">
                  <h3 className="text-base font-semibold text-spott-blue">Guarda esta cotización en tu portal</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Crea tu cuenta con Google para ver el estado de tus cotizaciones, retomar borradores y duplicarlas.
                  </p>
                  <button onClick={() => startLogin("/portal")} data-testid="wizard-login-btn"
                    className="mt-4 rounded-full bg-spott-blue px-6 py-3 text-sm font-bold text-white">
                    Crear cuenta / Iniciar sesión
                  </button>
                </div>
              )}
              {user && quote && (
                <button onClick={() => navigate(`/cotizacion/${quote.quote_id}`)} data-testid="go-to-quote-btn"
                  className="mt-8 text-sm font-semibold text-spott-blue underline">
                  Ver esta cotización en mi portal
                </button>
              )}
            </motion.section>
          )}

          {/* nav */}
          <div className="mt-10 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              data-testid="wizard-back-btn"
              className="inline-flex items-center gap-2 rounded-full border border-spott-mid bg-white px-6 py-3 text-sm font-medium text-spott-blue transition-colors hover:border-spott-yellow disabled:opacity-40">
              <ArrowLeft size={16} /> Atrás
            </button>
            {step < 5 && (
              <button onClick={next} data-testid="wizard-next-btn" disabled={loadingRecs}
                className="group inline-flex items-center gap-2 rounded-full bg-spott-yellow px-7 py-3.5 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03] disabled:opacity-60">
                {step === 4 ? "Ver mi cotización" : "Continuar"}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            )}
          </div>
        </main>

        {/* sticky cart */}
        <aside className="lg:col-span-4">
          <div className="sticky top-[150px] rounded-xl border border-spott-mid bg-white p-6" data-testid="quote-cart">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-spott-blue">
              <ShoppingCart size={16} className="text-spott-yellow" /> Tu cotización
            </h2>
            <div className="mt-5 space-y-3">
              {selectedSpaces.length === 0 && (
                <p className="text-sm text-gray-500">Aún no has seleccionado espacios.</p>
              )}
              {selectedSpaces.map((s) => (
                <div key={s.space_id} className="border-b border-spott-mid pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-spott-blue">{s.name}</div>
                      <div className="text-xs text-gray-500">{s.retailer_name} · {s.city}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-sm font-semibold text-spott-blue">
                        {money(s.price_month * unitsOf(s.space_id))}
                      </span>
                      <button onClick={() => toggleSpace(s.space_id)}
                        data-testid={`cart-remove-${s.space_id}`} aria-label="Quitar"
                        className="text-gray-400 transition-colors hover:text-red-500"><X size={14} /></button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => setUnits(s.space_id, unitsOf(s.space_id) - 1)}
                      data-testid={`cart-units-minus-${s.space_id}`} aria-label="Menos"
                      className="flex h-6 w-6 items-center justify-center rounded-full border border-spott-mid text-spott-blue">
                      <Minus size={11} />
                    </button>
                    <span className="text-xs font-bold text-spott-blue" data-testid={`cart-units-${s.space_id}`}>
                      {unitsOf(s.space_id)}
                    </span>
                    <button onClick={() => setUnits(s.space_id, unitsOf(s.space_id) + 1)}
                      data-testid={`cart-units-plus-${s.space_id}`} aria-label="Más"
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-spott-yellow text-spott-blue">
                      <Plus size={11} />
                    </button>
                    <span className="text-xs text-gray-400">× {money(s.price_month)}/mes</span>
                  </div>
                </div>
              ))}
            </div>
            {serviceIds.length > 0 && (
              <p className="mt-4 text-xs text-gray-500">{serviceIds.length} servicio(s) adicional(es)</p>
            )}
            <div className="mt-5 space-y-2 border-t border-spott-mid pt-5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Espacios</span><span>{spaceIds.length} ({totalUnits} unidades)</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Duración</span><span>{months} {months === 1 ? "mes" : "meses"}</span>
              </div>
              {calc?.discount_amount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Descuento (hasta {calc.discount_percent}%)</span><span>-{money2(calc.discount_amount)}</span>
                </div>
              )}
              {calc?.service_fee > 0 && (
                <div className="flex justify-between text-gray-600" data-testid="cart-fee">
                  <span>Fee Spott ({calc.service_fee_percent}%)</span><span>{money2(calc.service_fee)}</span>
                </div>
              )}
            </div>
            <div className="mt-5 rounded-lg bg-spott-blue p-5">
              <div className="text-xs font-light text-spott-mid">Total estimado (USD)</div>
              <div className="mt-1 text-3xl font-bold text-spott-yellow" data-testid="cart-total">
                {money2(calc?.total || 0)}
              </div>
              <div className="mt-1 text-xs font-light text-spott-mid">
                {money2(calc?.monthly_cost || 0)} por mes · {num(calc?.total_reach || 0)} impactos
              </div>
              {calc?.points_earned > 0 && (
                <div className="mt-3 flex items-center gap-1.5 border-t border-white/10 pt-3 text-xs font-medium text-spott-yellow"
                  data-testid="cart-points">
                  <Coins size={13} /> Ganarías {num(calc.points_earned)} puntos Spott
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={emailModal} onOpenChange={setEmailModal}>
        <DialogContent data-testid="email-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Enviar cotización por correo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Correo electrónico">
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="tu@empresa.com" data-testid="email-input" />
            </Field>
            <button onClick={sendEmail} disabled={busy || !emailTo} data-testid="email-submit-btn"
              className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue disabled:opacity-60">
              {busy ? "Enviando…" : "Enviar cotización"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={agentModal} onOpenChange={setAgentModal}>
        <DialogContent data-testid="agent-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Habla con un agente comercial</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nombre y apellido">
              <Input value={lead.contact_name} data-testid="lead-name-input"
                onChange={(e) => setLead({ ...lead, contact_name: e.target.value })} />
            </Field>
            <Field label="Correo electrónico">
              <Input value={lead.contact_email} data-testid="lead-email-input"
                onChange={(e) => setLead({ ...lead, contact_email: e.target.value })} />
            </Field>
            <Field label="Teléfono / WhatsApp">
              <Input value={lead.contact_phone} data-testid="lead-phone-input"
                onChange={(e) => setLead({ ...lead, contact_phone: e.target.value })} />
            </Field>
            <Field label="Mensaje (opcional)">
              <Textarea value={lead.message} data-testid="lead-message-input"
                onChange={(e) => setLead({ ...lead, message: e.target.value })} rows={3} />
            </Field>
            <button onClick={requestAgent} disabled={busy} data-testid="lead-submit-btn"
              className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue disabled:opacity-60">
              {busy ? "Enviando…" : "Solicitar contacto"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
